import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface CompletionStatusProps {
  isVisible: boolean;
  onClose: () => void;
  onComplete: () => void;
  title: string;
}

type StatusType = 'loading' | 'success' | 'error' | 'warning';

export default function CompletionStatus({ isVisible, onClose, onComplete, title }: CompletionStatusProps) {
  const [status, setStatus] = useState<StatusType>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isVisible) {
      setStatus('loading');
      setMessage('Processing your request...');
      
      // Simulate API call
      const timer = setTimeout(() => {
        // Simulate random success/failure for demo
        const success = Math.random() > 0.2; // 80% success rate
        
        if (success) {
          setStatus('success');
          setMessage(`${title} created successfully!`);
          setTimeout(() => {
            onComplete();
          }, 2000);
        } else {
          setStatus('error');
          setMessage('Failed to create job. Please check your connection and try again.');
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isVisible, title, onComplete]);

  if (!isVisible) return null;

  const getIcon = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="completion-spinner">
            <div className="spinner"></div>
          </div>
        );
      case 'success':
        return <CheckCircleIcon className="w-12 h-12 text-green-500" />;
      case 'error':
        return <XCircleIcon className="w-12 h-12 text-red-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-12 h-12 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusClass = () => {
    switch (status) {
      case 'success':
        return 'completion-success';
      case 'error':
        return 'completion-error';
      case 'warning':
        return 'completion-warning';
      default:
        return 'completion-loading';
    }
  };

  return (
    <div className="completion-overlay">
      <div className={`completion-modal ${getStatusClass()}`}>
        <div className="completion-icon">
          {getIcon()}
        </div>
        
        <div className="completion-content">
          <h3 className="completion-title">
            {status === 'loading' && 'Processing...'}
            {status === 'success' && 'Success!'}
            {status === 'error' && 'Error'}
            {status === 'warning' && 'Warning'}
          </h3>
          <p className="completion-message">{message}</p>
        </div>

        {status !== 'loading' && (
          <div className="completion-actions">
            {status === 'error' && (
              <button
                onClick={() => setStatus('loading')}
                className="btn btn-primary"
              >
                Try Again
              </button>
            )}
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              {status === 'success' ? 'Done' : 'Close'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
